/**
 * Admin → Algemene mailboxen → Log (recht admin_settings): laatste rondes, concepten, interne mails en
 * PO-acties. Alleen metadata — geen onderwerpen, namen of mailtekst.
 */
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { mailboxApi } from './api';
import { tellingTekst } from '../../InfoVerwerken/InfoVerwerken';

const GRIJS = '#556071';
const RAND = '#e5e9eb';
const tijd = (s) => (s ? new Date(`${s}${/Z|[+-]\d\d:?\d\d$/.test(s) ? '' : 'Z'}`).toLocaleString('nl-NL', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '');

export function rondeTekst(s) {
  if (!s || typeof s !== 'object') return '';
  if (s.overgeslagen) return String(s.overgeslagen);
  return ['werving', 'po', 'info', 'info_samenvatting'].filter((k) => s[k]).map((k) => {
    const d = s[k];
    if (d.overgeslagen) return `${k}: ${d.overgeslagen}`;
    if (d.fout) return `${k}: fout`;
    const delen = Object.entries(d).filter(([, v]) => typeof v === 'number' && v > 0).map(([n, v]) => `${n} ${v}`);
    return `${k}${d.modus ? ` (${d.modus})` : ''}: ${delen.join(', ') || 'niets nieuws'}`;
  }).join(' · ') || (s.afwijkingen !== undefined ? `PO-dagcheck: ${s.afwijkingen} afwijking(en)` : '');
}

function Lijst({ titel, kolommen, rijen }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1 }}>{titel}</Typography>
      {rijen.length === 0 ? (
        <Typography sx={{ fontSize: '0.84rem', color: GRIJS }}>Nog niets.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: RAND, fontSize: '0.8rem', py: 0.75 } }}>
            <TableHead>
              <TableRow>{kolommen.map(([k]) => <TableCell key={k} sx={{ color: GRIJS, fontWeight: 600 }}>{k}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {rijen.map((r, i) => (
                <TableRow key={i}>{kolommen.map(([k, f]) => <TableCell key={k}>{f(r)}</TableCell>)}</TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default function MailboxLog() {
  const [d, setD] = useState(null);
  useEffect(() => { mailboxApi.log().then(setD).catch((e) => { toast.error(e.message); setD({}); }); }, []);
  if (!d) return <CircularProgress size={20} sx={{ color: '#adb3b5' }} />;
  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Typography sx={{ fontSize: '0.84rem', color: GRIJS }}>Alleen metadata: geen onderwerpen, namen of mailtekst.</Typography>
      <Lijst titel="Rondes" rijen={(d.rondes || []).slice(0, 30)}
        kolommen={[['Tijd', (r) => tijd(r.op)], ['Soort', (r) => r.soort], ['Uitkomst', (r) => rondeTekst(r.samenvatting)]]} />
      <Lijst titel="Concepten (ikwilwerken@)" rijen={d.concepten || []}
        kolommen={[['Ontvangen', (r) => tijd(r.ontvangen_op)], ['Modus', (r) => r.modus], ['Categorie', (r) => r.categorie || '—'],
          ['Concept', (r) => r.draft_status || '—'], ['Interne mail', (r) => r.mail_status || '—']]} />
      <Lijst titel="Interne mails" rijen={d.interne_mails || []}
        kolommen={[['Tijd', (r) => tijd(r.op)], ['Soort', (r) => r.soort], ['Status', (r) => r.status], ['Ontvangers', (r) => r.ontvangers]]} />
      <Lijst titel="PO-acties" rijen={d.po_acties || []}
        kolommen={[['Tijd', (r) => tijd(r.op)], ['Door', (r) => r.door], ['Actie', (r) => `${r.actie}${r.ingetrokken ? ' (hersteld)' : ''}`],
          ['PO', (r) => r.po], ['Project', (r) => r.project || '—'], ['Gripp', (r) => r.gripp_waarde || '—']]} />
      <Lijst titel="info@ verwerkt via de samenvatting" rijen={d.info_verwerkt || []}
        kolommen={[['Tijd', (r) => tijd(r.op)], ['Door', (r) => r.door], ['Categorie', (r) => r.groep],
          ['Actie', (r) => `${r.actie}${r.modus === 'dry' ? ' (proef)' : ''}`], ['Uitkomst', (r) => `${tellingTekst(r.telling)}${r.status !== 'klaar' ? ` · ${r.status}` : ''}`]]} />
    </Box>
  );
}
